import { PropsWithChildren } from "react";
import {
  createPathComponent,
  LeafletContextInterface,
  PathProps,
} from "@react-leaflet/core";
import L from "leaflet";
import Hotline from "leaflet-hotline";
import { Coords } from "types/app";
// initialize UMD module
Hotline(L);

/** Coordinates with the value that controls the color of the line at that point */
type HotlineDataPoint = [...Coords, number | null];
interface HotlineProps {
  positions: HotlineDataPoint[];
  weight: number;
  outlineWidth: number;
  interactive: boolean;
  min: number;
  max: number;
}

type PathPropshWithChildren = PropsWithChildren<HotlineProps> & PathProps;

/** The parts of the Leaflet.hotline layer that we use */
type HotlineLayer = L.Polyline & {
  options: L.PolylineOptions & { min: number; max: number };
  _renderer: L.Canvas & { _update: () => void };
};

// The hotline reads the value from the altitude of each point, where Leaflet
// turns a missing value into 0 anyway - here it's just done explicitly
const toLatLngTuples = (positions: HotlineDataPoint[]): L.LatLngTuple[] =>
  positions.map(([lat, lng, value]) => [lat, lng, value ?? 0]);

const renderer = L.Hotline.renderer({ pane: "hotlinePane" });
const createPolyline = (
  { positions, ...options }: PathPropshWithChildren,
  context: LeafletContextInterface
) => {
  const instance = L.hotline(toLatLngTuples(positions), {
    ...options,
    renderer: renderer ?? undefined,
  }) as HotlineLayer;
  return { instance, context: { ...context, overlayContainer: instance } };
};

const updatePolyline = (
  layer: HotlineLayer,
  props: PathPropshWithChildren,
  prevProps: PathPropshWithChildren
) => {
  if (props.positions !== prevProps.positions) {
    layer.setLatLngs(toLatLngTuples(props.positions));
  }

  if (props.max !== prevProps.max || props.min !== prevProps.min) {
    layer.options.min = props.min;
    layer.options.max = props.max;
    layer._renderer._update();
  }
};

export const HotlinePolyline = createPathComponent(
  createPolyline,
  updatePolyline
);
